import { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";

type BeforeInstallPromptEvent = Event & {
  readonly platforms?: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const isStandaloneDisplay = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export function PwaInstallButton() {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isStandaloneDisplay());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();

      if (isStandaloneDisplay()) {
        setIsInstalled(true);
        return;
      }

      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      return;
    }

    await installPromptEvent.prompt();

    const choice = await installPromptEvent.userChoice;
    if (choice?.outcome !== "accepted") {
      // Allow the browser to fire beforeinstallprompt again on refresh by clearing the saved event reference.
      setInstallPromptEvent(null);
    }
  };

  if (!installPromptEvent || isInstalled) {
    return null;
  }

  return (
    <Tooltip title="Install this app for a full-screen experience">
      <IconButton color="inherit" onClick={handleInstallClick} aria-label="Install this app" size="small">
        <DownloadForOfflineIcon />
      </IconButton>
    </Tooltip>
  );
}
